import { arrayMove } from "@dnd-kit/sortable";

export interface OrderedSection {
  id: string;
  position: number;
}

export interface OrderedQuestion {
  id: string;
  sectionId: string;
  position: number;
}

// Move `activeId` to where `overId` sits, renumber 1..N, return the items whose
// position actually changed (to PATCH).
export const reorderSections = <T extends OrderedSection>(
  sections: T[],
  activeId: string,
  overId: string
): { next: T[]; changed: T[] } => {
  const oldIndex = sections.findIndex((s) => s.id === activeId);
  const newIndex = sections.findIndex((s) => s.id === overId);
  if (oldIndex < 0 || newIndex < 0) return { next: sections, changed: [] };
  const next = arrayMove(sections, oldIndex, newIndex).map((s, i) => ({
    ...s,
    position: i + 1,
  }));
  const changed = next.filter(
    (s) => s.position !== sections.find((p) => p.id === s.id)?.position
  );
  return { next, changed };
};

// Renumber questions 1..N within each section, preserving original array order.
export const normalizeQuestionPositions = <T extends OrderedQuestion>(
  items: T[]
): T[] => {
  const grouped: Record<string, T[]> = {};
  items.forEach((q) => {
    (grouped[q.sectionId] ??= []).push(q);
  });
  const out: T[] = [];
  Object.keys(grouped).forEach((sectionId) => {
    grouped[sectionId].forEach((q, i) => out.push({ ...q, position: i + 1 }));
  });
  return items.map((orig) => out.find((o) => o.id === orig.id) as T);
};

// Apply a question drag. `overIsSection` true when dropped on a section
// container (overId is the section id); otherwise overId is a question id.
export const applyQuestionDrag = <T extends OrderedQuestion>(
  questions: T[],
  activeId: string,
  overId: string,
  overIsSection: boolean
): { next: T[]; changed: T[] } => {
  const active = questions.find((q) => q.id === activeId);
  if (!active) return { next: questions, changed: [] };

  let updated: T[] = [...questions];

  if (overIsSection) {
    if (active.sectionId === overId) return { next: questions, changed: [] };
    updated = questions.map((q) =>
      q.id === activeId ? { ...q, sectionId: overId } : q
    );
  } else {
    const over = questions.find((q) => q.id === overId);
    if (!over || over.id === active.id) return { next: questions, changed: [] };
    const oldIndex = questions.findIndex((q) => q.id === activeId);
    const newIndex = questions.findIndex((q) => q.id === overId);
    updated = arrayMove(questions, oldIndex, newIndex);
    if (active.sectionId !== over.sectionId) {
      updated = updated.map((q) =>
        q.id === activeId ? { ...q, sectionId: over.sectionId } : q
      );
    }
  }

  const next = normalizeQuestionPositions(updated);
  const changed = next.filter((q) => {
    const prev = questions.find((p) => p.id === q.id);
    return (
      !prev || prev.position !== q.position || prev.sectionId !== q.sectionId
    );
  });
  return { next, changed };
};
